/**
 * Load tileset images from URL or local file.
 * Validates files before loading — size, type, and dimensions.
 */

const MAX_IMAGE_SIZE = 20 * 1024 * 1024; // 20 MB — professional tilesets can be large
const MAX_IMAGE_DIMENSION = 32768; // px — browser max is typically 16384-32768 depending on GPU
const ACCEPTED_TYPES = ['image/png', 'image/webp'];

export class TilesetLoader {
  constructor(state) {
    this.state = state;
  }

  /** Add a tileset from a local file with pre-upload validation. */
  async addFromFile() {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.png,.webp';
      input.addEventListener('change', async () => {
        const file = input.files[0];
        if (!file) { resolve(null); return; }

        // Validate before doing anything
        const error = this._validateFile(file);
        if (error) {
          this._showError(error);
          resolve(null);
          return;
        }

        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = async () => {
          // Validate dimensions
          const dimError = this._validateDimensions(img, file.name);
          if (dimError) {
            URL.revokeObjectURL(url);
            this._showError(dimError);
            resolve(null);
            return;
          }

          // Ask user for tile size
          const size = await this._promptTileSize(img, file.name);
          if (!size) { URL.revokeObjectURL(url); resolve(null); return; }

          const ts = this._createTilesetFromImage(img, file.name, url, size.tileW, size.tileH);
          resolve(ts);
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          this._showError('Could not load image. Make sure it\'s a valid PNG or WebP file.');
          resolve(null);
        };
        img.src = url;
      });
      input.click();
    });
  }

  /**
   * Add a tileset from a Blob (e.g., loaded from IndexedDB/CloudStorage).
   * @param {Blob} blob — image blob
   * @param {string} name — display name
   * @param {string} imagePath — storage path (used as key in tilesetImages)
   * @returns {Promise<Object|null>}
   */
  async addFromBlob(blob, name, imagePath) {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);
      img.onload = async () => {
        URL.revokeObjectURL(url);
        const dimError = this._validateDimensions(img, name);
        if (dimError) {
          this._showError(dimError);
          resolve(null);
          return;
        }
        // Re-create an object URL for the tilesetImages map (needs to stay alive)
        const persistUrl = URL.createObjectURL(blob);
        const ts = this._createTilesetFromImage(img, name, imagePath);
        resolve(ts);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    });
  }

  /** Add a tileset from a URL. */
  async addFromURL(url, name) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const ts = this._createTilesetFromImage(img, name || url, url);
        resolve(ts);
      };
      img.onerror = () => reject(new Error(`Failed to load: ${url}`));
      img.src = url;
    });
  }

  _validateFile(file) {
    // Type check
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return `"${file.name}" is a ${file.type || 'unknown'} file.\n\n`
        + 'TileForge accepts PNG and WebP images only.\n\n'
        + 'If you\'re using Photoshop or GIMP, export as PNG.\n'
        + 'If this is a Tiled TMX/TSX file, use File > Open instead.';
    }

    // Size check
    if (file.size > MAX_IMAGE_SIZE) {
      const sizeKB = Math.round(file.size / 1024);
      const limitKB = Math.round(MAX_IMAGE_SIZE / 1024);
      return `"${file.name}" is ${sizeKB} KB — the limit is ${limitKB} KB.\n\n`
        + 'Tileset spritesheets are typically 5-100 KB. If your file is larger:\n\n'
        + '\u2022 Make sure it\'s a spritesheet, not a full map render or screenshot\n'
        + '\u2022 Use indexed color (8-bit) instead of RGBA (32-bit)\n'
        + '\u2022 Check your export settings — disable metadata/thumbnails\n'
        + '\u2022 Run it through a PNG optimizer (tinypng.com, optipng)';
    }

    return null;
  }

  _validateDimensions(img, fileName) {
    const tileW = this.state.get('tileWidth');
    const tileH = this.state.get('tileHeight');

    // Too large
    if (img.width > MAX_IMAGE_DIMENSION || img.height > MAX_IMAGE_DIMENSION) {
      return `"${fileName}" is ${img.width}x${img.height} pixels — max supported is ${MAX_IMAGE_DIMENSION}x${MAX_IMAGE_DIMENSION}.\n\n`
        + 'This image exceeds the maximum canvas size supported by most browsers.\n'
        + 'Try using a smaller version of the tileset, or split it into multiple sheets.';
    }

    // Not divisible by tile size
    const cols = Math.floor(img.width / tileW);
    const rows = Math.floor(img.height / tileH);
    if (cols === 0 || rows === 0) {
      return `"${fileName}" is ${img.width}x${img.height} but your tile size is ${tileW}x${tileH}.\n\n`
        + 'The image is too small to contain even one tile.\n'
        + 'Either resize the image or change your map\'s tile size.';
    }

    const remainderX = img.width % tileW;
    const remainderY = img.height % tileH;
    if (remainderX !== 0 || remainderY !== 0) {
      // Warning, not blocking — some tilesets have margins
      console.warn(
        `Tileset "${fileName}" (${img.width}x${img.height}) doesn't divide evenly by tile size (${tileW}x${tileH}). `
        + `${remainderX}px extra width, ${remainderY}px extra height. This may indicate margin/spacing — configure in tileset settings.`
      );
    }

    return null;
  }

  _showError(message) {
    // Use a styled dialog instead of raw alert
    const overlay = document.createElement('div');
    overlay.className = 'dialog-overlay';
    const dialog = document.createElement('div');
    dialog.className = 'dialog';
    dialog.innerHTML = `
      <h2 class="dialog-title">Cannot Load Tileset</h2>
      <pre style="white-space:pre-wrap;font-size:12px;color:var(--text);margin:12px 0;line-height:1.5;">${message}</pre>
      <div class="dialog-buttons">
        <button class="btn btn-primary" id="tf-err-ok">OK</button>
      </div>
    `;
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
    dialog.querySelector('#tf-err-ok').addEventListener('click', () => overlay.remove());
    dialog.querySelector('#tf-err-ok').focus();
  }

  /**
   * Detect the best tile size for an image by testing common sizes.
   * Returns the size that divides both dimensions cleanly and produces
   * a reasonable number of tiles.
   */
  _detectTileSize(imgWidth, imgHeight) {
    const candidates = [8, 16, 24, 32, 48, 64, 96, 128];
    const mapTileW = this.state.get('tileWidth');
    const mapTileH = this.state.get('tileHeight');

    // Prefer sizes that divide both dimensions cleanly
    const clean = candidates.filter(s =>
      imgWidth % s === 0 && imgHeight % s === 0 && imgWidth / s >= 2 && imgHeight / s >= 2
    );

    if (clean.length > 0) {
      // Prefer the map's tile size if it's in the clean list
      if (clean.includes(mapTileW) && mapTileW === mapTileH) return { width: mapTileW, height: mapTileH };
      // Otherwise pick the largest clean size that gives at least 4 tiles
      for (let i = clean.length - 1; i >= 0; i--) {
        const tiles = (imgWidth / clean[i]) * (imgHeight / clean[i]);
        if (tiles >= 4) return { width: clean[i], height: clean[i] };
      }
      return { width: clean[0], height: clean[0] };
    }

    // No clean fit — fall back to map tile size
    return { width: mapTileW, height: mapTileH };
  }

  /**
   * Show a tile size picker dialog and return the chosen size.
   * @returns {Promise<{tileW: number, tileH: number}|null>} null if cancelled
   */
  _promptTileSize(img, name) {
    return new Promise((resolve) => {
      const detected = this._detectTileSize(img.width, img.height);
      const overlay = document.createElement('div');
      overlay.className = 'dialog-overlay';
      const dialog = document.createElement('div');
      dialog.className = 'dialog';
      dialog.style.minWidth = '380px';

      dialog.innerHTML = `
        <h2 class="dialog-title">Tileset Tile Size</h2>
        <p style="font-size:12px;color:var(--text-muted);margin-bottom:12px;">
          "${name.replace(/\.[^.]+$/, '')}" is ${img.width}&times;${img.height}px.
          Choose the tile size for this tileset.
        </p>
        <div class="form-group">
          <label class="form-label">Tile Size</label>
          <div class="form-row" style="gap:8px;align-items:center;">
            <select id="tf-ts-preset" class="form-input" style="flex:1;">
              <option value="8">8 &times; 8</option>
              <option value="16">16 &times; 16</option>
              <option value="24">24 &times; 24</option>
              <option value="32">32 &times; 32</option>
              <option value="48">48 &times; 48</option>
              <option value="64">64 &times; 64</option>
              <option value="96">96 &times; 96</option>
              <option value="128">128 &times; 128</option>
              <option value="custom">Custom...</option>
            </select>
          </div>
        </div>
        <div class="form-row" id="tf-ts-custom-row" style="display:none;gap:8px;align-items:center;margin-bottom:12px;">
          <input type="number" id="tf-ts-w" class="form-input" style="width:70px;" min="1" max="512" value="${detected.width}">
          <span style="color:var(--text-muted);">&times;</span>
          <input type="number" id="tf-ts-h" class="form-input" style="width:70px;" min="1" max="512" value="${detected.height}">
          <span style="color:var(--text-muted);font-size:11px;">px</span>
        </div>
        <div id="tf-ts-info" style="font-size:11px;color:var(--text-muted);margin-bottom:12px;"></div>
        <div class="dialog-buttons">
          <button class="btn btn-primary" id="tf-ts-ok">Load Tileset</button>
          <button class="btn" id="tf-ts-cancel">Cancel</button>
        </div>
      `;

      overlay.appendChild(dialog);
      document.body.appendChild(overlay);

      const presetSelect = dialog.querySelector('#tf-ts-preset');
      const customRow = dialog.querySelector('#tf-ts-custom-row');
      const wInput = dialog.querySelector('#tf-ts-w');
      const hInput = dialog.querySelector('#tf-ts-h');
      const info = dialog.querySelector('#tf-ts-info');

      // Set initial selection to detected size
      if ([8,16,24,32,48,64,96,128].includes(detected.width) && detected.width === detected.height) {
        presetSelect.value = String(detected.width);
      } else {
        presetSelect.value = 'custom';
        customRow.style.display = '';
      }

      const updateInfo = () => {
        let tw, th;
        if (presetSelect.value === 'custom') {
          tw = parseInt(wInput.value, 10) || 16;
          th = parseInt(hInput.value, 10) || 16;
        } else {
          tw = th = parseInt(presetSelect.value, 10);
        }
        const cols = Math.floor(img.width / tw);
        const rows = Math.floor(img.height / th);
        const remX = img.width % tw;
        const remY = img.height % th;
        let text = `${cols} &times; ${rows} = ${cols * rows} tiles`;
        if (remX || remY) text += ` (${remX}px extra width, ${remY}px extra height)`;
        info.innerHTML = text;
      };

      presetSelect.addEventListener('change', () => {
        customRow.style.display = presetSelect.value === 'custom' ? '' : 'none';
        updateInfo();
      });
      wInput.addEventListener('input', updateInfo);
      hInput.addEventListener('input', updateInfo);
      updateInfo();

      dialog.querySelector('#tf-ts-ok').addEventListener('click', () => {
        let tw, th;
        if (presetSelect.value === 'custom') {
          tw = parseInt(wInput.value, 10) || 16;
          th = parseInt(hInput.value, 10) || 16;
        } else {
          tw = th = parseInt(presetSelect.value, 10);
        }
        overlay.remove();
        resolve({ tileW: tw, tileH: th });
      });

      dialog.querySelector('#tf-ts-cancel').addEventListener('click', () => {
        overlay.remove();
        resolve(null);
      });
    });
  }

  _createTilesetFromImage(img, name, imagePath, tileW, tileH) {
    if (!tileW) tileW = this.state.get('tileWidth');
    if (!tileH) tileH = this.state.get('tileHeight');
    const columns = Math.floor(img.width / tileW);
    const rows = Math.floor(img.height / tileH);
    const tileCount = columns * rows;

    const tilesets = this.state.get('tilesets');
    let firstGid = 1;
    if (tilesets.length > 0) {
      const last = tilesets[tilesets.length - 1];
      firstGid = last.firstGid + last.tileCount;
    }

    const tsData = {
      firstGid,
      name: name.replace(/\.[^.]+$/, ''),
      image: imagePath,
      imageWidth: img.width,
      imageHeight: img.height,
      tileWidth: tileW,
      tileHeight: tileH,
      tileCount,
      columns,
      margin: 0,
      spacing: 0,
    };

    tilesets.push(tsData);
    const images = this.state.get('tilesetImages');
    images.set(imagePath, img);

    this.state._notify('tilesets', tilesets, tilesets);
    this.state._notify('tilesetImages', images, images);

    return tsData;
  }
}
